import React, {useState} from "react";
import {Button, Empty, Input, List, Switch, Tabs, Tag, Timeline} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {TICKET_MAX_NOTE_LENGTH} from "constants/ticket";
import {formatDateTime, getActorLabel, isOwnerNoteVisibilityLocked} from "../common/ticketUtils";

const getHistoryText = (item, t, roleLabels) => {
    if (item.kind === "status") {
        return t(`detail.history.status.${item.status}`, {ns: "tickets"});
    }
    const roles = []
        .concat(item.role || [])
        .map(role => roleLabels[role] || role)
        .join(", ");
    if (!item.actor) {
        return t("detail.history.autoAssigned", {ns: "tickets", roles});
    }
    return item.type === "user"
        ? t("detail.history.assignedUser", {ns: "tickets", name: item.userName || "-"})
        : t("detail.history.assignedRole", {ns: "tickets", roles});
};

export const TicketHistoryTabs = ({
    ticket,
    history,
    notes,
    roleLabels,
    customerView,
    canAddNote,
    onChanged
}) => {
    const {t} = useTranslation(["tickets"]);
    const stores = useStore();
    const [note, setNote] = useState("");
    const [showToOwner, setShowToOwner] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isLocked = isOwnerNoteVisibilityLocked(ticket);

    const handleAddNote = async () => {
        setSubmitting(true);
        const result = await stores.ticketStore.addTicketNote(ticket._id, {
            note: note.trim(),
            isShownToOwner: !isLocked && showToOwner
        });
        setSubmitting(false);
        if (result) {
            setNote("");
            setShowToOwner(false);
            onChanged();
        }
    };

    const historyItems = [...history]
        .filter(item => !customerView || item.kind === "status")
        .reverse();

    return (
        <Tabs defaultActiveKey="notes">
            <Tabs.TabPane
                tab={t("detail.tabs.notes", {ns: "tickets", count: notes.length})}
                key="notes"
            >
                {notes.length ? (
                    <List
                        dataSource={notes}
                        renderItem={item => (
                            <List.Item key={item._id}>
                                <div style={{width: "100%"}}>
                                    <div style={{display: "flex", justifyContent: "space-between"}}>
                                        <strong>{getActorLabel(item.createdBy, roleLabels)}</strong>
                                        <span style={{color: "rgba(0, 0, 0, 0.45)", fontSize: 12}}>
                                            {formatDateTime(item.createdAt)}
                                        </span>
                                    </div>
                                    {!customerView && item.isShownToOwner && (
                                        <Tag color="green" style={{margin: "4px 0"}}>
                                            {t("detail.notes.visibleToOwner", {ns: "tickets"})}
                                        </Tag>
                                    )}
                                    <div style={{whiteSpace: "pre-wrap"}}>{item.note}</div>
                                </div>
                            </List.Item>
                        )}
                    />
                ) : (
                    <Empty description={t("detail.notes.empty", {ns: "tickets"})} />
                )}
                {canAddNote && (
                    <div style={{marginTop: 16}}>
                        <Input.TextArea
                            rows={3}
                            maxLength={TICKET_MAX_NOTE_LENGTH}
                            value={note}
                            onChange={event => setNote(event.target.value)}
                            placeholder={t("detail.notes.placeholder", {ns: "tickets"})}
                        />
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: 8
                            }}
                        >
                            <span>
                                <Switch
                                    checked={showToOwner}
                                    disabled={isLocked}
                                    onChange={setShowToOwner}
                                />{" "}
                                {t("detail.notes.showToOwner", {ns: "tickets"})}
                            </span>
                            <Button
                                type="primary"
                                loading={submitting}
                                disabled={!note.trim()}
                                onClick={handleAddNote}
                            >
                                {t("detail.notes.add", {ns: "tickets"})}
                            </Button>
                        </div>
                    </div>
                )}
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("detail.tabs.history", {ns: "tickets"})} key="history">
                {historyItems.length ? (
                    <Timeline>
                        {historyItems.map((item, index) => (
                            <Timeline.Item key={`${item.kind}-${index}`}>
                                <div style={{fontWeight: 500}}>
                                    {getHistoryText(item, t, roleLabels)}
                                </div>
                                <div style={{color: "rgba(0, 0, 0, 0.45)", fontSize: 12}}>
                                    {item.actor
                                        ? getActorLabel(item.actor, roleLabels)
                                        : t("list.system", {ns: "tickets"})}{" "}
                                    · {formatDateTime(item.createdAt)}
                                </div>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                ) : (
                    <Empty description={t("detail.history.empty", {ns: "tickets"})} />
                )}
            </Tabs.TabPane>
        </Tabs>
    );
};
